import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { CellNotificationsActions } from 'src/features/parkingManagement/components/Notifications/CellNotificationsActions';
import { type Notification, NOTIFICATIONS } from 'src/features/parkingManagement/types/notifications';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils/messages';
import { MEMBERSHIP_ROLES } from 'src/features/userManagement/types';
import { membershipRoleOptions } from 'src/features/userManagement/utils';
import { DataTableColumnGenericHeaderDefinition, DataTableColumnHeader } from 'src/registry/components/table';
import { Badge } from 'src/registry/ui/Badge';
import { generalMessages } from 'src/utils/messages';
const columnHelper = createColumnHelper<Notification>();

export const notificationTitleMapping = {
    [NOTIFICATIONS.RESERVATION_CREATED_BY_CLIENT_NOTIFY_OPERATOR]: {
        title: parkingManagementMessages.newReservation,
        subTitle: parkingManagementMessages.createClientReservationNotification,
    },
    [NOTIFICATIONS.RESERVATION_CANCELLED_BY_CLIENT_NOTIFY_OPERATOR]: {
        title: parkingManagementMessages.cancelledReservation,
        subTitle: parkingManagementMessages.cancelClientReservationNotification,
    },
};

export const NotificationsTableColumns = ({
    openNotificationModal,
}: {
    openNotificationModal: (notification: Notification) => void;
}) => {
    const { formatMessage } = useIntl();

    return [
        columnHelper.accessor('notification_type', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div>
                        <div className="font-bold whitespace-nowrap">
                            {formatMessage(notificationTitleMapping[row.original.notification_type].title)}
                        </div>
                        <div className="text-sm text-gray-600">
                            {formatMessage(notificationTitleMapping[row.original.notification_type].subTitle)}
                        </div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
            size: 460,
            minSize: 320,
            maxSize: 460,
        }),
        columnHelper.accessor('is_enabled', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const isEnabled = row.original.is_enabled;
                const badgeTheme = row.original.is_enabled ? 'emerald' : 'red';
                const badgeText = isEnabled
                    ? formatMessage(parkingManagementMessages.enabled)
                    : formatMessage(parkingManagementMessages.disabled);
                const badgeIcon = isEnabled ? faCheckCircle : faXmarkCircle;

                return (
                    <Badge theme={badgeTheme}>
                        <FontAwesomeIcon icon={badgeIcon} />
                        {badgeText}
                    </Badge>
                );
            },
            meta: {
                title: formatMessage(parkingManagementMessages.enabled),
            },
        }),
        columnHelper.accessor('recipient_roles', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                const labels = row.original.recipient_roles
                    .map((recipient: MEMBERSHIP_ROLES) => {
                        const label = membershipRoleOptions.find((role) => role.value === recipient)?.label;
                        return label ? formatMessage(label) : null;
                    })
                    .filter(Boolean);

                return <div>{labels.join(', ')}</div>;
            },
            meta: {
                title: formatMessage(parkingManagementMessages.recipientRoles),
            },
        }),
        columnHelper.display({
            header: DataTableColumnGenericHeaderDefinition,
            id: 'actions',
            cell: (props) => <CellNotificationsActions notificationCallBack={openNotificationModal} {...props} />,
        }),
    ];
};
